import * as THREE from 'three'
import { TransformControls } from 'three/addons/controls/TransformControls.js'
import { DEG2RAD } from 'three/src/math/MathUtils.js'

import { EditorAssetLayers } from './EditorAssetLayers'

const LMB = 1 // bitmask
const RMB = 2 // bitmask

export class EditorAssetGizmo {
  constructor(studio) {
    this.studio = studio
    this.scene = studio.scene
    this.camera = studio.camera
    this.viewport = studio.viewport
    this.viewport.addEventListener('pointerdown', this.onPointerDown)
    this.viewport.addEventListener('pointerup', this.onPointerUp)
    this.selected = null
    this.control = new TransformControls(this.camera, this.viewport)
    this.control.size = 0.5
    this.control.addEventListener('objectChange', this.onControlChange)
    this.control.addEventListener('mouseDown', this.onControlDown)
    this.control.addEventListener('mouseUp', this.onControlUp)
    this.gizmo = this.control.getHelper()
    this.scene.add(this.gizmo)
    this.pointer = new THREE.Vector2()
    this.raycaster = new THREE.Raycaster()
    this.raycaster.layers.set(EditorAssetLayers.BOX)
    this.obj = null
  }

  updatePointer(clientX, clientY) {
    const rect = this.viewport.getBoundingClientRect()
    // Support mouse, touch, or pointer events
    // const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX
    // const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? e.changedTouches?.[0]?.clientY
    const x = (clientX - rect.left) / rect.width
    const y = (clientY - rect.top) / rect.height
    // Convert to NDC: [-1, 1]
    this.pointer.x = x * 2 - 1
    this.pointer.y = -(y * 2 - 1)
  }

  select(obj, silent) {
    if (this.obj === obj) return
    this.obj = obj
    if (obj) {
      this.control.attach(obj)
    } else {
      this.control.detach()
    }
    if (!silent) {
      this.studio.emit('selected', obj)
    }
  }

  setType(type) {
    if (type === 'move') {
      this.control.mode = 'translate'
    }
    if (type === 'rotate') {
      this.control.mode = 'rotate'
    }
    if (type === 'scale') {
      this.control.mode = 'scale'
    }
    if (type === 'resize') {
      console.warn('todo: resize')
    }
  }

  setSpace(space) {
    this.control.space = space
  }

  setSnap(snap) {
    this.control.setTranslationSnap(snap ? 0.5 : 0)
    this.control.setRotationSnap(snap ? 5 * DEG2RAD : 0)
    this.control.setScaleSnap(snap ? 0.1 : 0)
  }

  onPointerDown = e => {
    const left = !!(e.buttons & LMB)
    if (!left) return
    // console.log('pointer down')
    this.downAt = performance.now()
  }

  onPointerUp = e => {
    // pose mode is locked on base, ignore re-selection
    if (this.studio.mode === 'pose') return

    // console.log('pointer up')
    if (!this.downAt) return
    const elapsed = performance.now() - this.downAt
    this.downAt = null
    if (elapsed > 300) return
    // raycast to find box and select it
    this.updatePointer(e.clientX, e.clientY)
    this.raycaster.setFromCamera(this.pointer, this.camera)
    const hits = this.raycaster.intersectObjects(this.scene.children)
    const hit = hits[0]

    // const nodeId = hit?.object.parent._nodeId
    this.select(hit?.object.parent)
  }

  onControlChange = e => {
    if (!this.obj) return
    this.studio.emit('transform', {
      obj: this.obj,
    })
  }

  onControlDown = e => {
    this.downAt = null
    // console.log('control down')
    this.studio.orbit.freeze = true
  }

  onControlUp = e => {
    // console.log('control up')
    this.studio.orbit.freeze = false
    this.studio.emit('transformEnd', {
      obj: this.obj,
    })
  }

  destroy() {
    this.viewport.removeEventListener('pointerdown', this.onPointerDown)
    this.viewport.removeEventListener('pointerup', this.onPointerUp)
    this.control.removeEventListener('change', this.onControlChange)
    this.control.removeEventListener('mouseDown', this.onControlDown)
    this.control.removeEventListener('mouseUp', this.onControlUp)
  }
}
